// Client for looking up runs by a Passio platform request id (its X-Request-ID, logged as req-id).
// Backend: GET /api/v2/analytics/requests/{id} in api/core/routers/analytics.py. Admins can pass customerId.

import { ApiError, TokenGetter } from './learningApi';

export const MAX_REQUEST_ID_CHARS = 128;

export interface RequestFunctionRun {
  id: string;
  function_id: string;
  function_name: string;
  function_version: number;
  success: boolean;
  model_used: string | null;
  execution_time_ms: number | null;
  created_at: string | null;
  error: string | null;
  /** Set when the run is a step of a chain execution. */
  chain_execution_id: string | null;
  node_id: string | null;
  mapping_index: number | null;
  content_redacted: boolean;
}

export interface RequestChainExecution {
  id: string;
  chain_id: string;
  chain_name: string | null;
  status: string;
  execution_time_ms: number | null;
  started_at: string | null;
  completed_at: string | null;
  error: string | null;
  content_redacted: boolean;
  source_execution_id: string | null;
}

export interface RequestLookupResult {
  request_id: string;
  customer_id: string;
  customer_tag: string | null;
  /** Oldest first, at most 500. */
  function_runs: RequestFunctionRun[];
  chain_executions: RequestChainExecution[];
  first_at: string | null;
  last_at: string | null;
  truncated: boolean;
}

const UUID_RE = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;
const LABEL_RE = /^["'`]?(?:x-request-id|req[-_]id|request[-_ ]?id|passio_request_id)["'`]?\s*[:=]\s*/i;
const EDGE_JUNK_RE = /^[\s"'`,;]+|[\s"'`,;]+$/g;

/**
 * The request id in pasted text: a bare id, a quoted id, or a header/log line such as
 * `req-id: <id>`, `X-Request-ID: <id>` or `"req-id":"<id>"`. A UUID anywhere in the text wins.
 */
export function parseRequestId(raw: string | null | undefined): string {
  const text = (raw || '').trim();
  if (!text) return '';
  const uuid = UUID_RE.exec(text);
  if (uuid) return uuid[0].toLowerCase();
  return text.replace(EDGE_JUNK_RE, '').replace(LABEL_RE, '').replace(EDGE_JUNK_RE, '').slice(0, MAX_REQUEST_ID_CHARS);
}

/** The UUID in `text` when it looks like a pasted platform request id, else null. */
export function requestIdIn(text: string | null | undefined): string | null {
  const match = UUID_RE.exec(text || '');
  return match ? match[0].toLowerCase() : null;
}

export function requestLookupPath(requestId: string, customerId?: string | null): string {
  const q = new URLSearchParams({ id: requestId });
  if (customerId) q.set('customer_id', customerId);
  return `/dashboard/request-lookup?${q}`;
}

export function requestLookupApi(getToken: TokenGetter) {
  return {
    lookup: async (requestId: string, customerId?: string | null): Promise<RequestLookupResult> => {
      const token = await getToken();
      const q = customerId ? `?${new URLSearchParams({ customer_id: customerId })}` : '';
      const res = await fetch(`/api/v2/analytics/requests/${encodeURIComponent(requestId)}${q}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        let message = res.statusText;
        try {
          const body = await res.json();
          message = typeof body.detail === 'string' ? body.detail : JSON.stringify(body.detail || body);
        } catch {
          /* keep statusText */
        }
        throw new ApiError(message, res.status);
      }
      return (await res.json()).data as RequestLookupResult;
    },
  };
}
